import { t } from '@lingui/macro';
import { formatToIso8601Date } from '@luminovo/commons';
import { CheckableChip, FieldDateControlled, Flexbox, FormItem } from '@luminovo/design-system';
import { FieldValues, Path, UseControllerProps, useController } from 'react-hook-form';
import { analytics } from '../../utils/analytics';

interface Props<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>
    extends UseControllerProps<TFieldValues, TName> {}

/**
 * Add calendar days to a date
 * @param date The starting date
 * @param days Number of calendar days to add
 * @returns A new date with the calendar days added
 */
function addCalendarDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export function DueDateFormItem<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>({
    name,
    control,
}: Props<TFieldValues, TName>): JSX.Element {
    const { field } = useController({ name, control });

    const quickAddOptions = [
        { label: t`In 2 calendar days`, handler: () => addCalendarDays(new Date(), 2) },
        { label: t`In 7 calendar days`, handler: () => addCalendarDays(new Date(), 7) },
        { label: t`In 14 calendar days`, handler: () => addCalendarDays(new Date(), 14) },
    ];

    // Calculate selectedOption during rendering instead of using useEffect
    const selectedOption = field.value
        ? quickAddOptions.find((option) => formatToIso8601Date(option.handler()) === field.value)?.label || null
        : null;

    const handleQuickAdd = (option: string, handler: () => Date) => {
        const newDate = handler();
        // Convert Date to ISO string format (YYYY-MM-DD) before updating the form field
        field.onChange(formatToIso8601Date(newDate));

        // Track the due date quick selection event
        analytics.track('due_date_quick_selection', {
            option,
            days: parseInt(option.match(/\+(\d+)/)?.[1] || '0', 10),
        });
    };

    return (
        <FormItem
            label={t`Due date`}
            actions={
                <Flexbox flexDirection="row" gap={8} alignItems="center" flexWrap="wrap">
                    {quickAddOptions.map((option) => (
                        <CheckableChip
                            key={option.label}
                            label={option.label}
                            selected={selectedOption === option.label}
                            onClick={() => handleQuickAdd(option.label, option.handler)}
                        />
                    ))}
                </Flexbox>
            }
        >
            <FieldDateControlled name={name} control={control} />
        </FormItem>
    );
}
